import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useStore } from '../lib/store';
import { ChevronRight, Check, Fish, UserPlus, RefreshCw } from 'lucide-react';
type Step = 'entry' | 'basics' | 'returning' | 'preferences' | 'done';
interface RegistrationFormProps {
  onSuccess?: () => void;
  isWalkIn?: boolean;
}
export function RegistrationForm({
  onSuccess,
  isWalkIn = false
}: RegistrationFormProps) {
  const [step, setStep] = useState<Step>('entry');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [city, setCity] = useState('');
  const [occupation, setOccupation] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [pickupPref, setPickupPref] = useState('');
  const {
    setUser,
    addReservation,
    activeEventId,
    registeredUsers
  } = useStore();
  const ageOptions = ['Under 18', '18–24', '25–34', '35–44', '45+'];
  const occupationOptions = ['Student', 'Arbetar', 'Arbetssökande', 'Pensionär', 'Annat'];
  const interestOptions = [{
    label: 'Snacks & godis',
    emoji: '🍿'
  }, {
    label: 'Dryck',
    emoji: '🥤'
  }, {
    label: 'Hälsa & träning',
    emoji: '💪'
  }, {
    label: 'Skönhet',
    emoji: '✨'
  }, {
    label: 'Tech',
    emoji: '📱'
  }, {
    label: 'Mode',
    emoji: '👕'
  }, {
    label: 'Upplevelser',
    emoji: '🎉'
  }, {
    label: 'Mat',
    emoji: '🍕'
  }];
  const pickupOptions = ['Instabox', 'Budbee', 'PostNord Box', 'Bring', 'Normal', 'ICA', 'Coop', 'Willys', 'Pressbyrån', 'Hemköp'];
  const handleBasicsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone || !ageRange || !city || !occupation) return;
    setStep('preferences');
  };
  const handleReturningSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !phone) return;
    const existingUser = registeredUsers.find(u => u.phone.replace(/\s/g, '') === phone.replace(/\s/g, ''));
    if (existingUser) {
      setAgeRange(existingUser.ageRange || '');
      setCity(existingUser.city || '');
      setOccupation(existingUser.occupation || '');
      setErrorMsg('');
      setStep('preferences');
    } else {
      setErrorMsg('Vi kunde inte hitta detta nummer. Är det din första catch?');
    }
  };
  const handlePreferencesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (interests.length === 0 || !pickupPref) return;
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    const existingUser = registeredUsers.find(u => u.phone.replace(/\s/g, '') === phone.replace(/\s/g, ''));
    setUser({
      phone,
      name,
      ageRange,
      city,
      interests,
      storePreference: pickupPref,
      points: existingUser ? existingUser.points : 0,
      level: existingUser ? existingUser.level : 'Beginner',
      eligible_for_next_event: false
    });
    if (isWalkIn && activeEventId) {
      addReservation(activeEventId, 'redeemed');
    }
    setIsLoading(false);
    setStep('done');
  };
  const toggleInterest = (interest: string) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else if (interests.length < 3) {
      setInterests([...interests, interest]);
    }
  };
  const stepNumber = step === 'basics' || step === 'returning' ? 1 : step === 'preferences' ? 2 : 3;
  const StepIndicator = () => step !== 'done' && step !== 'entry' ? <div className="flex items-center justify-center space-x-2 mb-6" data-id="element-17">
        {[1, 2].map(s => <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s <= stepNumber ? 'w-8 bg-blue-500' : 'w-8 bg-gray-200'}`} data-id="element-18" />)}
      </div> : null;
  // --- ENTRY ---
  if (step === 'entry') {
    return <div className="space-y-4 w-full animate-in fade-in duration-300" data-id="element-19">
        <div className="text-center mb-6" data-id="element-20">
          <h2 className="text-xl font-black text-gray-900" data-id="element-21">Välkommen!</h2>
          <p className="text-gray-500 text-sm" data-id="element-22">
            Är det din första catch eller har du fångat förut?
          </p>
        </div>

        <button onClick={() => setStep('basics')} className="w-full bg-blue-500 hover:bg-blue-600 text-white p-6 rounded-2xl flex flex-col items-center justify-center space-y-3 transition-all shadow-lg shadow-blue-500/30" data-id="element-23">
          <UserPlus className="w-8 h-8" data-id="element-24" />
          <span className="font-bold text-lg" data-id="element-25">Första fångsten</span>
          <span className="text-blue-100 text-xs" data-id="element-26">
            Jag har inte registrerat mig tidigare
          </span>
        </button>

        <button onClick={() => setStep('returning')} className="w-full bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-50 p-6 rounded-2xl flex flex-col items-center justify-center space-y-3 transition-all" data-id="element-27">
          <RefreshCw className="w-8 h-8" data-id="element-28" />
          <span className="font-bold text-lg" data-id="element-29">Fånga ny catch</span>
          <span className="text-blue-500/70 text-xs" data-id="element-30">
            Jag har redan ett konto
          </span>
        </button>
      </div>;
  }
  // --- RETURNING ---
  if (step === 'returning') {
    return <form onSubmit={handleReturningSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-31">
        <StepIndicator data-id="element-32" />
        <div className="text-center" data-id="element-33">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-34">
            Välkommen tillbaka
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-35">
            Ange dina uppgifter för att hämta ny catch
          </p>
        </div>

        <div className="space-y-4" data-id="element-36">
          <div data-id="element-37">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-38">
              Namn
            </label>
            <Input placeholder="Ditt namn" value={name} onChange={e => setName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-39" />
          </div>

          <div data-id="element-40">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-41">
              Telefonnummer
            </label>
            <Input placeholder="07X XXX XX XX" value={phone} onChange={e => setPhone(e.target.value)} type="tel" required className="bg-gray-50 border-gray-200" data-id="element-42" />
          </div>

          {errorMsg && <p className="text-red-500 text-xs text-center font-medium" data-id="element-43">
              {errorMsg}
            </p>}
        </div>

        <Button type="submit" className="w-full" data-id="element-44">
          Nästa <ChevronRight className="w-4 h-4 ml-1" data-id="element-45" />
        </Button>

        <button type="button" onClick={() => setStep('entry')} className="w-full text-gray-400 text-xs font-medium mt-2" data-id="element-46">
          Tillbaka
        </button>
      </form>;
  }
  // --- BASICS ---
  if (step === 'basics') {
    return <form onSubmit={handleBasicsSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-47">
        <StepIndicator data-id="element-48" />
        <div className="text-center" data-id="element-49">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-50">Lite om dig</h2>
          <p className="text-gray-400 text-sm" data-id="element-51">
            Så vi vet vem som fångade boxen
          </p>
        </div>

        <div className="space-y-4" data-id="element-52">
          <div data-id="element-53">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-54">
              Namn
            </label>
            <Input placeholder="Ditt namn" value={name} onChange={e => setName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-55" />
          </div>

          <div data-id="element-56">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-57">
              Telefonnummer
            </label>
            <Input placeholder="07X XXX XX XX" value={phone} onChange={e => setPhone(e.target.value)} type="tel" required className="bg-gray-50 border-gray-200" data-id="element-58" />
          </div>

          <div data-id="element-59">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-60">
              Ålder
            </label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-id="element-61">
              {ageOptions.map(opt => <button key={opt} type="button" onClick={() => setAgeRange(opt)} className={`py-2 px-1 rounded-lg text-sm font-medium border transition-all ${ageRange === opt ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-62">
                  {opt}
                </button>)}
            </div>
          </div>

          <div data-id="element-63">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-64">
              Sysselsättning
            </label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-id="element-65">
              {occupationOptions.map(opt => <button key={opt} type="button" onClick={() => setOccupation(opt)} className={`py-2 px-1 rounded-lg text-sm font-medium border transition-all ${occupation === opt ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-66">
                  {opt}
                </button>)}
            </div>
          </div>

          <div data-id="element-67">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1" data-id="element-68">
              Stad / Postnummer
            </label>
            <Input placeholder="t.ex. Stockholm" value={city} onChange={e => setCity(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-69" />
          </div>
        </div>

        <Button type="submit" className="w-full" data-id="element-70">
          Nästa <ChevronRight className="w-4 h-4 ml-1" data-id="element-71" />
        </Button>

        <button type="button" onClick={() => setStep('entry')} className="w-full text-gray-400 text-xs font-medium mt-2" data-id="element-72">
          Tillbaka
        </button>
      </form>;
  }
  // --- PREFERENCES ---
  if (step === 'preferences') {
    return <form onSubmit={handlePreferencesSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-73">
        <StepIndicator data-id="element-74" />

        <div className="text-center" data-id="element-75">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-76">
            <Fish className="w-6 h-6 text-blue-600" data-id="element-77" />
          </div>
          <h2 className="text-lg font-bold text-gray-900" data-id="element-78">
            Vad vill du fiska mer av?
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-79">
            Välj upp till 3 kategorier så matchar vi rätt drops till dig
          </p>
        </div>

        <div className="space-y-5" data-id="element-80">
          <div data-id="element-81">
            <div className="flex flex-wrap gap-2" data-id="element-82">
              {interestOptions.map(opt => <button key={opt.label} type="button" onClick={() => toggleInterest(opt.label)} className={`py-2.5 px-4 rounded-full text-sm font-medium border transition-all flex items-center space-x-1.5 ${interests.includes(opt.label) ? 'bg-blue-500 text-white border-blue-500 shadow-sm shadow-blue-500/30' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-83">
                  <span data-id="element-84">{opt.emoji}</span>
                  <span data-id="element-85">{opt.label}</span>
                </button>)}
            </div>
            {interests.length > 0 && <p className="text-xs text-blue-500 font-medium mt-2 ml-1" data-id="element-86">
                {interests.length}/3 valda
              </p>}
          </div>

          <div data-id="element-87">
            <label className="text-xs font-bold text-gray-500 uppercase ml-1 mb-2 block" data-id="element-88">
              Var vill du hämta ut nästa catch?
            </label>
            <select value={pickupPref} onChange={e => setPickupPref(e.target.value)} className="w-full p-3 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-blue-500 outline-none text-gray-700" required data-id="element-89">
              <option value="" disabled data-id="element-90">
                Välj upphämtningsplats...
              </option>
              {pickupOptions.map(opt => <option key={opt} value={opt} data-id="element-91">
                  {opt}
                </option>)}
            </select>
          </div>
        </div>

        <div className="flex items-start space-x-2 text-gray-500 text-xs" data-id="element-92">
          <input type="checkbox" required className="mt-0.5 rounded text-blue-500" defaultChecked data-id="element-93" />
          <span data-id="element-94">Jag godkänner SMS-uppdateringar om framtida drops</span>
        </div>

        <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-95">
          Slutför & fånga din box
        </Button>
      </form>;
  }
  // --- DONE ---
  if (step === 'done') {
    return <div className="space-y-6 w-full animate-in zoom-in duration-300 text-center" data-id="element-96">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto" data-id="element-97">
          <Check className="w-8 h-8 text-green-600" data-id="element-98" />
        </div>

        <div data-id="element-99">
          <h2 className="text-2xl font-black text-gray-900 mb-1" data-id="element-100">
            Catch registrerad!
          </h2>
          <p className="text-gray-500 text-sm" data-id="element-101">Din box är säkrad. Njut!</p>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-left space-y-2" data-id="element-102">
          <p className="text-sm font-bold text-blue-900" data-id="element-103">Vad händer nu?</p>
          <ul className="text-xs text-blue-700 space-y-1.5" data-id="element-104">
            <li className="flex items-start" data-id="element-105">
              <span className="mr-2" data-id="element-106">📦</span>Öppna och testa produkterna
            </li>
            <li className="flex items-start" data-id="element-107">
              <span className="mr-2" data-id="element-108">⭐</span>Lämna en review i appen för att
              låsa upp nästa drop
            </li>
            <li className="flex items-start" data-id="element-109">
              <span className="mr-2" data-id="element-110">📲</span>Du får SMS när nästa catch är redo
            </li>
          </ul>
        </div>

        <Button onClick={() => onSuccess && onSuccess()} className="w-full" data-id="element-111">
          Klar!
        </Button>
      </div>;
  }
  return null;
}